// SensorSummaryCard.js
import React, { useState } from "react";
import {
  ChevronRight,
  ChevronDown,
  Activity,
  Wind,
  Building,
  CheckCircle,
  XCircle,
  Plug,
  Battery,
  BatteryFull,
  BatteryMedium,
  BatteryLow,
  Target,
} from "lucide-react";
 
// Icon map by category
const iconMap = {
  occupancy: <Target className="text-gray-500" />,
  iaq: <Wind className="text-gray-500" />,
  pag: <Building className="text-gray-500" />,
  plug: <Plug className="text-gray-500" />,
  acupressure: <Target className="text-gray-500" />,
};
 
// Sensor connected if updated within 24h
const isSensorConnected = (timestamp) => {
  if (!timestamp || timestamp === 0) return false;
  const updatedTime = new Date(timestamp);
  const now = new Date();
  const diffInMs = now - updatedTime;
  return diffInMs / (1000 * 60 * 60) <= 24;
};
 
const SensorSummaryCard = ({ title, count, icon, sensors = [], defaultExpanded = false, onUpdateSensor }) => {
  const [isExpanded, setIsExpanded] = useState(title === "Occupancy Sensors" ? true : defaultExpanded);
  const [expandedSensorId, setExpandedSensorId] = useState(null);
  const [editingSensor, setEditingSensor] = useState(null);
  const [formValues, setFormValues] = useState({ area: "", areaNumber: "" });
 
  const toggleSensorDetails = (sensorId) => {
    setExpandedSensorId((prev) => (prev === sensorId ? null : sensorId));
  };
 
  const openEditModal = (sensor) => {
    setEditingSensor(sensor);
    setFormValues({
      area: sensor.area || "",
      areaNumber: sensor.areaNumber || "",
    });
  };
 
  const closeEditModal = () => {
    setEditingSensor(null);
  };
 
  const handleSave = () => {
    if (editingSensor && onUpdateSensor) {
      onUpdateSensor(editingSensor.id, {
        area: formValues.area,
        areaNumber: formValues.areaNumber,
      });
    }
    closeEditModal();
  };
 
  return (
    <div className="bg-white rounded-lg shadow border transition">
      {/* Header */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-50"
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100">
            {iconMap[icon] || <Activity className="text-gray-500" />}
          </div>
          <div>
            <h2 className="text-lg font-medium">{title}</h2>
            <p className="text-sm text-gray-500">{count} devices</p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronDown className="text-gray-400" />
        ) : (
          <ChevronRight className="text-gray-400" />
        )}
      </div>
 
 
      {/* Sensor List */}
      {isExpanded && (
        <div className="divide-y">
          {sensors.length === 0 ? (
            <p className="text-sm text-gray-500 px-4 py-2">No sensors found.</p>
          ) : (
            sensors.map((sensor) => {
              const connected = isSensorConnected(sensor.timestamp);
 
              return (
                <div key={sensor.id}>
                  {/* Row */}
                  <div
                    onClick={() => toggleSensorDetails(sensor.id)}
                    className="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-50"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        {connected ? (
                          <CheckCircle className="text-green-500" size={16} />
                        ) : (
                          <XCircle className="text-red-500" size={16} />
                        )}
                        <span className="font-medium text-sm">{sensor.id}</span>
                      </div>
                      <p className="text-sm text-gray-500">{sensor.area}</p>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      {sensor.deviceType === "milesight" ? (() => {
                        const pct = typeof sensor.battery === "number" ? sensor.battery : null;
                        const color = pct === null
                          ? "text-gray-500"
                          : pct >= 60
                          ? "text-green-500"
                          : pct >= 20
                          ? "text-yellow-500"
                          : "text-red-500";
                        const BatteryIcon = pct === null
                          ? Battery
                          : pct >= 80
                          ? BatteryFull
                          : pct >= 40
                          ? BatteryMedium
                          : BatteryLow;
                        return (
                          <>
                            <BatteryIcon size={16} className={color} />
                            <span className="text-gray-600">{pct ?? "N/A"}%</span>
                          </>
                        );
                      })() : (
                        <>
                          <Plug size={16} className="text-gray-500" />
                          <span className="text-gray-600">POE</span>
                        </>
                      )}
                      {expandedSensorId === sensor.id ? (
                        <ChevronDown className="text-gray-400" />
                      ) : (
                        <ChevronRight className="text-gray-400" />
                      )}
                    </div>
                  </div>
 
                  {/* Expanded Sensor Detail */}
                  {expandedSensorId === sensor.id && (
                    <div className="bg-gray-50 px-6 py-4 text-sm text-gray-700 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-gray-500">Device Type</p>
                        <p className="font-medium">{sensor.deviceType || "Unknown"}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Status</p>
                        <p className="font-medium flex items-center gap-2">
                          {connected ? (
                            <CheckCircle className="text-green-500" size={16} />
                          ) : (
                            <XCircle className="text-red-500" size={16} />
                          )}
                          <span className={connected ? "text-green-600" : "text-red-600"}>
                            {connected ? "Connected" : "Disconnected"}
                          </span>
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Location</p>
                        <p className="font-medium">{sensor.area}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Area</p>
                        <p className="font-medium">{sensor.areaNumber || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Power</p>
                        <p className="font-medium flex items-center gap-2">
                          {sensor.deviceType === "milesight" ? (() => {
                            const pct = typeof sensor.battery === "number" ? sensor.battery : null;
                            const color = pct === null
                              ? "text-gray-600"
                              : pct >= 60
                              ? "text-green-500"
                              : pct >= 20
                              ? "text-yellow-500"
                              : "text-red-500";
                            const BatteryIcon = pct === null
                              ? Battery
                              : pct >= 80
                              ? BatteryFull
                              : pct >= 40
                              ? BatteryMedium
                              : BatteryLow;
                            return (
                              <>
                                <BatteryIcon size={16} className={color} />
                                <span>{pct ?? "N/A"}%</span>
                              </>
                            );
                          })() : (
                            <>
                              <Plug size={16} className="text-gray-600" />
                              <span>POE</span>
                            </>
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Last Updated</p>
                        <p className="font-medium">
                          {sensor.timestamp ? new Date(sensor.timestamp).toLocaleString() : "N/A"}
                        </p>
                      </div>
 
                      {/* IAQ Sensor Fields */}
                      {sensor.temperature !== undefined && (
                        <div>
                          <p className="text-gray-500">Temperature (°C)</p>
                          <p className="font-medium">{sensor.temperature}</p>
                        </div>
                      )}
                      {sensor.co2 !== undefined && (
                        <div>
                          <p className="text-gray-500">CO₂ (ppm)</p>
                          <p className="font-medium">{sensor.co2}</p>
                        </div>
                      )}
                      {sensor.pm25 !== undefined && (
                        <div>
                          <p className="text-gray-500">PM2.5 (µg/m³)</p>
                          <p className="font-medium">{sensor.pm25}</p>
                        </div>
                      )}
 
                      {/* Edit */}
                      <div className="col-span-1 md:col-span-2 text-right">
                        <button onClick={() => openEditModal(sensor)} className="px-4 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">
                          Edit
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
      {editingSensor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={closeEditModal}></div>
          <div className="relative bg-white w-full max-w-lg rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Edit Device</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={closeEditModal}>×</button>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-gray-500 text-sm">Device ID</p>
                <input type="text" className="w-full border rounded px-3 py-2 bg-gray-100" value={editingSensor.id} readOnly />
              </div>
              <div>
                <p className="text-gray-500 text-sm">Device Type</p>
                <input type="text" className="w-full border rounded px-3 py-2 bg-gray-100" value={editingSensor.deviceType || ""} readOnly />
              </div>
              <div>
                <p className="text-gray-500 text-sm">Location</p>
                <input
                  type="text"
                  className="w-full border rounded px-3 py-2"
                  value={formValues.area}
                  onChange={(e) => setFormValues((v) => ({ ...v, area: e.target.value }))}
                />
              </div>
              <div>
                <p className="text-gray-500 text-sm">Area</p>
                <input
                  type="text"
                  className="w-full border rounded px-3 py-2"
                  value={formValues.areaNumber}
                  onChange={(e) => setFormValues((v) => ({ ...v, areaNumber: e.target.value }))}
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button className="px-4 py-2 rounded border" onClick={closeEditModal}>Cancel</button>
              <button className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700" onClick={handleSave}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
 
export default SensorSummaryCard;
 
 
 
 
 